import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { ClickButton } from "../components/Buttons";
import { TableUI } from "../components/Tables";
import { TextInputForm, LoadingSpinner } from "../components/Forms";
import { useNavigate, Navigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaMagnifyingGlass } from "react-icons/fa6";
import * as XLSX from "xlsx";
import API_DOMAIN from "../config/config";
import companyId from "../config/companyId";

const DummyTablehead = [
  "S.No",
  "Party Name",
  "Mobile Number",
  "Address",
  "Status",
];

const Party = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(true);

  const fetchData = useCallback(async (searchQuery = "") => {
    setLoading(true);
    try {
      const partyResponse = await fetch(`${API_DOMAIN}/sales_party.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          search_text: searchQuery,
          company_id: companyId,
        }),
      });
      if (!partyResponse.ok) throw new Error("Failed to fetch party data");
      const partyData = await partyResponse.json();

      if (partyData.code === 200) {
        if (!partyData.data?.length) {
          throw new Error("No customer data returned");
        }
        const formattedData = partyData.data.map((party) => ({
          party_name: String(party.party_name || "N/A"),
          mobile_number: String(party.mobile_number || "N/A"),
          billing_address: String(party.billing_address || "N/A"),
        }));
        setUserData(formattedData);
      } else {
        throw new Error(partyData.msg || "Failed to fetch customer data");
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
      toast.error("Failed to load customer data. Please try again.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData(searchText);
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchText, fetchData]);

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleClear = () => {
    setSearchText("");
  };

  const handleExcelDownload = () => {
    const exportData = filteredUserData.map((item, index) => ({
      "S.No": index + 1,
      "Party Name": item.party_name || "N/A",
      "Mobile Number": item.mobile_number || "N/A",
      Address: item.billing_address || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "customers");
    XLSX.writeFile(workbook, "customers.xlsx");
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const session = localStorage.getItem("session");
        if (!session) {
          setIsLoggedIn(false);
        } else {
          fetchData();
        }
      } catch (error) {
        console.error("Error checking session:", error.message);
        setIsLoggedIn(false);
      }
    };
    checkSession();
  }, [fetchData]);

  // Filter data client-side
  const filteredUserData = userData.filter((item) =>
    String(item.party_name || "")
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div>
      <Container fluid>
        <Row>
          <Col lg="12" className="py-1">
            <div className="main my-2">
              <Row className="mx-2">
                <Col lg="3">
                  <div className="mx-2">
                    <TextInputForm
                      placeholder={"Search Party Name"}
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                      prefix_icon={<FaMagnifyingGlass />}
                      labelname={"Search"}
                      onClear={handleClear}
                    />
                  </div>
                </Col>
                <Col
                  lg="12"
                  className="align-item-baseline d-flex justify-content-end"
                >
                  <div className="me-2">
                    <ClickButton
                      label={"Excel"}
                      onClick={handleExcelDownload}
                    />
                  </div>
                </Col>
              </Row>
              <div className="py-2 c-table table-scroll">
                {loading ? (
                  <LoadingSpinner
                    message="Loading Customer Data..."
                    height="300px"
                  />
                ) : (
                  <TableUI
                    headers={DummyTablehead}
                    body={filteredUserData}
                    type="partysales"
                    pageview="yes"
                    onDelete={fetchData}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default Party;
